import { useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';

import {
  SIDEMENU_WIDTH,
  SIDEMENU_Z_INDEX,
} from 'components/shared/side-menu/side-menu.constants';

import Icons from 'assets/icons';

interface BaseSideMenuProps {
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  headerSlot?: React.ReactNode;
  topSlot?: React.ReactNode;
  children?: React.ReactNode;
  footerSlot?: React.ReactNode;
  className?: string;
  widthConfig?: typeof SIDEMENU_WIDTH;
  zIndexConfig?: typeof SIDEMENU_Z_INDEX;
  ariaLabel?: string;
}

const BaseSideMenu = ({
  isCollapsed = false,
  onToggleCollapsed,
  headerSlot,
  topSlot,
  children,
  footerSlot,
  className,
  widthConfig = SIDEMENU_WIDTH,
  zIndexConfig = SIDEMENU_Z_INDEX,
  ariaLabel = 'Main navigation',
}: BaseSideMenuProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const triggerFade = useCallback(() => {
    const el = contentRef.current;

    if (!el) return;

    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    triggerFade();
  }, [isCollapsed, triggerFade]);

  const sideMenuWidth = isCollapsed
    ? widthConfig.collapsedClass
    : widthConfig.expandedClass;

  return (
    <aside
      className={classNames(
        'h-screen bg-white border-r border-navy-200 flex flex-col transition-[width] duration-300 ease-in-out fixed left-0 top-0',
        sideMenuWidth,
        className
      )}
      style={{ zIndex: zIndexConfig.menu }}
      aria-label={ariaLabel}
      data-testid="side-menu-container"
    >
      {headerSlot && (
        <div
          className={classNames(
            'flex items-center p-3 shrink-0',
            isCollapsed ? 'justify-center' : 'justify-between'
          )}
        >
          {headerSlot}

          {!isCollapsed && onToggleCollapsed && (
            <button
              onClick={onToggleCollapsed}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-600"
              aria-label="Collapse menu"
              aria-expanded="true"
              data-testid="side-menu-toggle-button"
            >
              <Icons.DoubleArrow
                className="size-3.5 rotate-180"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )}

      {isCollapsed && onToggleCollapsed && (
        <button
          onClick={onToggleCollapsed}
          className="absolute top-5 -right-3 size-6 bg-white border border-navy-200 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 shadow-sm"
          style={{ zIndex: zIndexConfig.expandButton }}
          aria-label="Expand menu"
          aria-expanded="false"
        >
          <Icons.DoubleArrow className="size-2" aria-hidden="true" />
        </button>
      )}

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div
          ref={contentRef}
          className={classNames(
            'flex flex-col h-full animate-sidebar-fade-in',
            !isCollapsed && 'min-w-64'
          )}
        >
          {topSlot}

          <div className="flex-1 overflow-y-auto custom-scroll">{children}</div>

          {footerSlot}
        </div>
      </div>
    </aside>
  );
};

export default BaseSideMenu;
